import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PhoneIncoming, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/common/Badges";
import { api, errorMessage } from "@/lib/api";
import { formatCurrency } from "@/lib/format";

const Notes = ({ pop }) => {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(pop.notes || "");
  const save = async () => {
    try { await api.post(`/phone/pops/${pop.id}/notes`, { notes }); toast.success("Call notes saved"); setOpen(false); } catch (e) { toast.error(errorMessage(e)); }
  };
  if (!open) return <Button size="sm" variant="outline" className="h-7" onClick={() => setOpen(true)} data-testid={`pop-log-notes-${pop.id}`}>Log notes</Button>;
  return (
    <div className="w-full space-y-1">
      <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was the call about?" data-testid={`pop-notes-input-${pop.id}`} />
      <Button size="sm" className="h-7" onClick={save} data-testid={`pop-notes-save-${pop.id}`}>Save notes</Button>
    </div>
  );
};

const PopCard = ({ pop, onDismiss }) => (
  <div className="w-80 rounded-sm border border-violet-500/40 bg-background shadow-lg p-3 space-y-2 text-sm animate-in slide-in-from-bottom-2" data-testid={`screen-pop-${pop.id}`}>
    <div className="flex items-start justify-between gap-2">
      <p className="flex items-center gap-2 font-semibold"><PhoneIncoming className="h-4 w-4 text-primary" />
        <span data-testid="screen-pop-title">Call from {pop.contact?.name || pop.caller_name || pop.number}</span></p>
      <button type="button" onClick={onDismiss} title="Dismiss" data-testid={`pop-dismiss-${pop.id}`}><X className="h-4 w-4 text-muted-foreground" /></button>
    </div>
    <p className="text-xs text-muted-foreground tabular-nums">{pop.number}{pop.contact?.draft && " · draft contact"}{pop.contact?.vip && " · VIP"}</p>
    {pop.contact ? <Link to={`/contacts/${pop.contact.id}`} className="text-xs text-primary hover:underline" data-testid={`pop-contact-link-${pop.id}`}>Open contact</Link>
      : <p className="text-xs text-muted-foreground">Unknown number. A draft contact is created when the call is logged.</p>}
    {pop.tickets.length > 0 && <div className="space-y-0.5" data-testid="pop-tickets"><p className="text-xs font-medium">Open tickets</p>
      {pop.tickets.map((t) => <Link key={t.id} to={`/tickets/${t.id}`} className="block truncate text-xs hover:underline" data-testid={`pop-ticket-${t.ref}`}><span className="tabular-nums font-medium">{t.ref}</span> {t.subject}</Link>)}</div>}
    {pop.orders.length > 0 && <div className="space-y-0.5" data-testid="pop-orders"><p className="text-xs font-medium">Recent orders</p>
      {pop.orders.map((o) => <p key={o.increment_id} className="text-xs tabular-nums">#{o.increment_id} · {o.status} · {formatCurrency(o.grand_total)}</p>)}</div>}
    <div className="flex flex-wrap gap-2"><Notes pop={pop} /><StatusBadge colour="violet" size="xs">Phone system</StatusBadge></div>
  </div>
);

export const ScreenPops = () => {
  const [pops, setPops] = useState([]);
  useEffect(() => {
    let alive = true;
    const load = () => api.get("/phone/pops").then((r) => alive && setPops(r.data.items)).catch(() => {});
    load();
    const id = setInterval(load, 3000);
    return () => { alive = false; clearInterval(id); };
  }, []);
  const dismiss = async (p) => { setPops((x) => x.filter((y) => y.id !== p.id)); await api.post(`/phone/pops/${p.id}/dismiss`).catch(() => {}); };
  if (!pops.length) return null;
  return <div className="fixed bottom-4 left-4 z-50 space-y-2 lg:left-60" data-testid="screen-pops">{pops.map((p) => <PopCard key={p.id} pop={p} onDismiss={() => dismiss(p)} />)}</div>;
};
